import { Select } from 'antd';
import type { SelectProps } from 'antd/es/select';
import { useEffect, useState } from 'react';
import { ChevronDownIcon } from '@/icons';
import { PLACEHOLDER_IMG } from '../../../../../../constants/global.constants';
import { cn } from '../../../../../../lib/cn';
import { firstChar } from '../../../../../../utils/string.utils';

type TData = {
  id: number | string;
  avatar?: string;
  name: string;
  empId?: number | string;
  role?: string;
};

type TSelect<ValueType = unknown> = {
  data?: TData[];
  className?: string;
  rootClassName?: string;
} & SelectProps<ValueType>;

export default function SelectWithInfo({
  data,
  className,
  rootClassName,
  onChange,
  ...rest
}: TSelect) {
  const [value, setValue] = useState<string | number | undefined>(undefined);

  // Clear value when data changes (when employee is filtered out after assignment)
  useEffect(() => {
    if (value && data && !data.find((emp) => emp.id === value)) {
      setValue(undefined);
    }
  }, [data, value]);

  const handleChange = (newValue: any, option: any) => {
    setValue(newValue);
    if (onChange) {
      onChange(newValue, option);
    }
    // Clear the selection after a brief moment
    setTimeout(() => {
      setValue(undefined);
    }, 100);
  };

  return (
    <Select
      placeholder="Assign to"
      className={cn('w-48', className)}
      size="small"
      suffixIcon={<ChevronDownIcon size={24} />}
      rootClassName={cn('small_dropdown', rootClassName)}
      showSearch
      optionFilterProp="search"
      value={value}
      onChange={handleChange}
      options={data?.map((emp) => ({
        value: emp?.id,
        search: `${emp?.name} ${emp?.empId || ''} ${emp?.role || ''}`,
        label: (
          <div className="flex gap-3 rounded-lg">
            {emp?.avatar ? (
              <img
                src={emp?.avatar || PLACEHOLDER_IMG}
                alt={emp?.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <span className="size-8 inline-flex justify-center items-center rounded-full border-[0.86px] dark:bg-gray-300 dark:border-gray-200 dark:text-theme-gray-800 font-semibold text-xs border-teal-500">
                {firstChar(emp?.name)}
              </span>
            )}

            <div className="text-left dark:text-white">
              {emp?.empId && (
                <p className="text-xs font-light">ID {emp.empId}</p>
              )}
              <p className="font-medium">{emp?.name}</p>
              {emp?.role && <p className="text-xs">{emp.role}</p>}
            </div>
          </div>
        ),
      }))}
      labelRender={(selected) => {
        const emp = data?.find((e) => e?.id === selected?.value);
        return emp ? emp?.name : undefined;
      }}
      allowClear
      popupMatchSelectWidth={300}
      {...rest}
    />
  );
}
